import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DOCUMENT,
  effect,
  HostListener,
  inject,
  OnDestroy,
  signal,
  OnInit,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AuthStateService } from '@core/services/auth/auth-state';
import { UserStore } from '@shared/stores/user';
import { SvgMenuClose } from '@shared/svgs/svg-menu-close/svg-menu-close';
import { SvgMenu } from '@shared/svgs/svg-menu/svg-menu';
import { SvgLogout } from '@shared/svgs/svg-logout/svg-logout';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  bootstrapBackpack,
  bootstrapHouse,
  bootstrapLock,
  bootstrapMoonFill,
  bootstrapPersonVcard,
  bootstrapPinMap,
  bootstrapSuitcaseLg,
  bootstrapSunFill,
  bootstrapCircleHalf,
} from '@ng-icons/bootstrap-icons';
import { ThemeMode } from '@shared/interfaces/IThemeStore';
import { ThemeStore } from '@shared/stores/theme.store';
import { CustomSelect } from '@shared/components/custom-select/custom-select';
import { SelectOption } from '@models/select-option';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-navbar',
  imports: [
    RouterLink,
    RouterLinkActive,
    CommonModule,
    SvgMenuClose,
    SvgMenu,
    SvgLogout,
    NgIcon,
    CustomSelect,
    FormsModule,
  ],
  viewProviders: [
    provideIcons({
      bootstrapHouse,
      bootstrapPinMap,
      bootstrapPersonVcard,
      bootstrapLock,
      bootstrapSuitcaseLg,
      bootstrapBackpack,
      bootstrapMoonFill,
      bootstrapSunFill,
      bootstrapCircleHalf,
    }),
  ],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
})
export class Navbar implements OnDestroy, OnInit {
  //injects
  private _authStateService = inject(AuthStateService);
  private _store = inject(UserStore);
  public _themeStore = inject(ThemeStore);
  private _doc = inject(DOCUMENT);
  //vars
  items = [
    {
      label: 'Página principal',
      icon: 'bootstrapHouse',
      bg: 'bg-blue-200',
      color: 'text-blue-600',
      route: '/home',
    },
    {
      label: 'Información básica y de contacto',
      icon: 'bootstrapPersonVcard',
      bg: 'bg-green-200',
      color: 'text-green-700',
      route: '/personal-info',
    },
    {
      label: 'Seguridad y acceso',
      icon: 'bootstrapLock',
      bg: 'bg-blue-200',
      color: 'text-blue-700',
      route: '/security',
    },
    {
      label: 'Direcciones',
      icon: 'bootstrapPinMap',
      bg: 'bg-[#a1c4ff]',
      color: 'text-blue-700',
      route: '/addresses',
    },
    {
      label: 'Datos escolares',
      icon: 'bootstrapBackpack',
      bg: 'bg-purple-200',
      color: 'text-purple-700',
      route: '/education',
    },
    {
      label: 'Datos laborales',
      icon: 'bootstrapSuitcaseLg',
      bg: 'bg-pink-200',
      color: 'text-pink-700',
      route: '/jobs',
    },
  ];
  private scrollY = 0;
  //signals
  active = signal(0);
  navOpen = signal(false);
  scrolled = signal(false);
  //computed
  public isDarkMode = computed(() => this._themeStore.isDarkMode());
  themeOptions = signal<SelectOption<ThemeMode>[]>([
    { value: 'light', label: 'Claro' },
    { value: 'dark', label: 'Oscuro' },
    { value: 'system', label: 'Sistema' },
  ]);
  themeMode: ThemeMode = 'system';

  constructor() {
    effect(() => {
      const open = this.navOpen();

      if (open) {
        this.scrollY = window.scrollY;
        const body = this._doc.body;

        body.style.position = 'fixed';
        body.style.top = `-${this.scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
      } else {
        const body = this._doc.body;

        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';

        window.scrollTo(0, this.scrollY);
      }
    });
  }

  ngOnInit() {
    this.themeMode = this._themeStore.mode();
  }

  private isThemeMode(value: unknown): value is ThemeMode {
    return value === 'light' || value === 'dark' || value === 'system';
  }

  onThemeModelChange(value: unknown) {
    if (!this.isThemeMode(value)) return;

    this.themeMode = value;
    this._themeStore.setThemeMode(value);
  }

  ngOnDestroy() {
    const body = this._doc.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
  }
  //hostlisteners
  @HostListener('window:scroll')
  onScroll() {
    this.scrolled.set(window.scrollY > 65);
  }

  onMenuClick(i: number) {
    this.active.set(i);
  }

  async logout() {
    await this._authStateService.logout();
    this._store.clearAuth();
  }

  onOverlayClick(e: MouseEvent) {
    if (e.target === e.currentTarget) this.navOpen.set(false);
  }

  protected readonly themeIcon = computed(() => {
    const mode = this._themeStore.mode();

    if (mode === 'dark') return 'bootstrapMoonFill';
    if (mode === 'light') return 'bootstrapSunFill';
    return 'bootstrapCircleHalf';
  });

  protected readonly themeLabel = computed(() => {
    const mode = this._themeStore.mode();

    if (mode === 'dark') return 'Tema oscuro';
    if (mode === 'light') return 'Tema claro';
    return 'Tema del sistema';
  });

  protected setTheme(mode: ThemeMode) {
    this._themeStore.setThemeMode(mode);
  }
}
